import * as React from 'react';
import { connect, Dispatch } from 'react-redux';
import { RootState } from 'src/reducers/RootReducer';
import Project from 'src/entities/Project';
import * as moment from 'moment';
import {
  indexSectorsOfInterest,
  IndexSectorsOfInterestAction
} from 'src/actions/sector-of-interest/index/IndexSectorsOfInterest.actions';
import SectorOfInterest from 'src/entities/SectorOfInterest';
import Base64Encoder from 'src/services/encoders/Base64Encoder';
import ProjectCompanyFormComponent,
{ ProjectCompanyFormComponentProps } from 'src/modules/project/update/partials/ProjectCompanyForm.component';
import { changeCompanyData, ChangeCompanyDataAction } from 'src/actions/company/change-data/ChangeCompanyData.actions';
import {
  updateCompany,
  UpdateCompanyAction,
  updateCompanyClearErrors,
  UpdateCompanySuccess
} from 'src/actions/company/update/UpdateCompany.actions';
import * as updateCompanyTypes from 'src/actions/company/update/Types';
import LoadingContainer from 'src/modules/commons/loading/Loading.container';
import CompanyData from 'src/value-objects/CompanyData';
import { getProject, GetProjectAction } from 'src/actions/project/get/GetProject.actions';
import * as getProjectTypes from 'src/actions/project/get/Types';

interface StateProps {
  formData?: CompanyData;
  error: boolean;
  errorMessage: {};
  sectorsOfInterest: SectorOfInterest[];
}

interface DispatchProps {
  updateCompanyClearErrors: () => UpdateCompanyAction;

  changeCompanyData(company: CompanyData): ChangeCompanyDataAction;

  updateCompany(company: CompanyData): Promise<UpdateCompanyAction>;

  getProject(projectId: number): Promise<GetProjectAction>;

  indexSectorsOfInterest(): Promise<IndexSectorsOfInterestAction>;
}

interface OwnProps {
  project: Project;
}

interface Props extends StateProps, DispatchProps, OwnProps {

}

interface State {
  success?: boolean;
  logo?: File;
}

class ProjectCompanyFormContainer extends React.Component<Props, State> {

  constructor(props: Props) {
    super(props);
    this.state = {};
    this.props.updateCompanyClearErrors();
  }

  public componentWillMount(): void {
    this.props.indexSectorsOfInterest();
    this.props.changeCompanyData(new CompanyData(this.props.project.company));
    this.setState(
      {success: false}
    );
  }

  public render(): React.ReactNode {
    return (
      this.props.project && this.props.formData ?
        <ProjectCompanyFormComponent {...this.getComponentProps()} /> :
        <LoadingContainer/>
    );
  }

  private handleChange(key: string): (value: string | boolean | File | SectorOfInterest) => void {
    switch (key) {
      case 'logo':
        return (
          (value: File) => {
            this.setState({logo: value});
            Base64Encoder.encode(value).then((result) => {
              let newImage = {
                url: URL.createObjectURL(value),
                encoded: result.split('base64,')[1],
                ext: value.type.split('/')[1],
                name: value.name
              };
              this.props.formData!.logo = newImage;
              this.props.changeCompanyData(new CompanyData(this.props.formData!));
            });
          }
        );
      case 'establishmentDay':
        return (
          (value: string) => {
            this.props.formData!.establishmentDate = moment(this.props.formData!.establishmentDate)
              .date(parseInt(value, 10));
            this.props.changeCompanyData(new CompanyData(this.props.formData!));
          }
        );
      case 'establishmentMonth':
        return (
          (value: string) => {
            value = (parseInt(value, 10) - 1).toString();
            this.props.formData!.establishmentDate = moment(this.props.formData!.establishmentDate).month(value);
            this.props.changeCompanyData(new CompanyData(this.props.formData!));
          }
        );
      case 'establishmentYear':
        return (
          (value: string) => {
            this.props.formData!.establishmentDate = moment(this.props.formData!.establishmentDate)
              .year(parseInt(value, 10));
            this.props.changeCompanyData(new CompanyData(this.props.formData!));
          }
        );
      case 'sectorsOfInterest':
        return (
          (value: SectorOfInterest) => {

            let newData = {...this.props.formData!};
            let index = newData.sectorsOfInterest.findIndex(sector => sector.id === value.id);
            if (index !== -1) {
              newData.sectorsOfInterest.splice(index, 1);
            } else {
              newData.sectorsOfInterest.push(value);
            }
            this.props.changeCompanyData(new CompanyData(newData));
          }
        );
      default:
        return (
          (value: string | boolean) => {
            let newData = {...this.props.formData!, [key]: value};
            this.props.changeCompanyData(new CompanyData(newData));
          }
        );
    }
  }

  private handleSubmit(): void {
    this.props.updateCompany(this.props.formData!).then((response: UpdateCompanySuccess) => {
      if (response.type === updateCompanyTypes.UPDATE_COMPANY_SUCCESS) {
        this.setState({logo: undefined});
        this.setState({success: true});
        setTimeout(
          () => {
            this.props.getProject(this.props.project.id).then((res) => {
                if (res.type === getProjectTypes.GET_PROJECT_SUCCESS) {
                  this.props.changeCompanyData(new CompanyData(this.props.project.company));
                }
              }
            );
          },
          2000
        );
      }
    });
  }

  private getComponentProps(): ProjectCompanyFormComponentProps {
    return {
      handleChange: (key: string) => this.handleChange(key),
      handleSubmit: () => this.handleSubmit(),
      formData: this.props.formData!,
      error: this.props.error,
      errorMessage: this.props.errorMessage,
      success: this.state.success!,
      sectorsOfInterest: this.props.sectorsOfInterest,
      logo: this.state.logo,
    };
  }
}

const mapDispatchToProps: (dispatch: Dispatch<RootState>) => DispatchProps = (
  dispatch: Dispatch<RootState>): DispatchProps => ({
  indexSectorsOfInterest: (): Promise<IndexSectorsOfInterestAction> => (
    dispatch(indexSectorsOfInterest())
  ),
  changeCompanyData: (company: CompanyData): ChangeCompanyDataAction => (
    dispatch(changeCompanyData(company))
  ),
  updateCompany: (company: CompanyData): Promise<UpdateCompanyAction> => (
    dispatch(updateCompany(company))
  ),
  getProject: (projectId: number): Promise<GetProjectAction> => (
    dispatch(getProject(projectId))
  ),
  updateCompanyClearErrors: (): UpdateCompanyAction => {
    return dispatch(updateCompanyClearErrors());
  }
});

const mapStateToProps: (state: RootState) => StateProps = (state: RootState): StateProps => ({
  formData: state.changeCompanyData.company,
  error: state.updateCompany.error,
  errorMessage: state.updateCompany.errorMessage,
  sectorsOfInterest: state.indexSectorOfInterest.sectorsOfInterest,
});

export default connect(mapStateToProps, mapDispatchToProps)(ProjectCompanyFormContainer);



// WEBPACK FOOTER //
// ./src/modules/project/update/partials/ProjectCompanyForm.container.tsx